import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from './ui/Icon.jsx';
import { updatesFor, latestUpdateNumber } from '../data/updates.js';

// ─── WHAT'S NEW ───────────────────────────────────────────────────────────────
// A header bell for platform updates, read from src/data/updates.js.
//
// WHY LOCALSTORAGE AND NOT THE RETAILER PROFILE
// "Have you seen update 3 yet" is a read-state question, and putting it on the
// profile would mean a write to Supabase on every open plus an RLS policy, for
// a badge. localStorage keeps it free and works for guests browsing a
// catalogue link, who have no profile to write to at all. The trade-off is
// that the badge is per-device: the same person on a phone and a laptop sees
// it twice. That is the right way round — showing a customer an update again
// costs nothing, silently hiding one costs them the feature.
//
// Only the highest number seen is stored, not a set of ids. Numbers only ever
// increase, so one integer answers "what is new for you" and cannot rot the
// way a growing list of ids would.

const SEEN_KEY = 'sedra_updates_seen';

const readSeen = () => {
  try {
    const n = parseInt(localStorage.getItem(SEEN_KEY), 10);
    return Number.isFinite(n) ? n : 0;
  } catch {
    // Safari in private mode throws on access rather than returning null.
    return 0;
  }
};

const writeSeen = (n) => {
  try { localStorage.setItem(SEEN_KEY, String(n)); } catch { /* not critical */ }
};

export default function WhatsNew({ lang = 'ar', isMobile = false }) {
  const isAr = lang === 'ar';
  const updates = useMemo(() => updatesFor('customers'), []);
  const latest  = useMemo(() => latestUpdateNumber('customers'), []);

  const [open, setOpen] = useState(false);
  // Read once on mount: localStorage is unavailable during SSR-style first
  // render in some browsers, and re-reading per render would thrash.
  const [seen, setSeen] = useState(0);
  useEffect(() => { setSeen(readSeen()); }, []);

  // A first-time visitor has seen nothing, but greeting them with "4 new
  // updates" is noise about a product they have never used. Treat an unset
  // marker as caught up, and only badge genuine changes from then on.
  useEffect(() => {
    if (updates.length === 0) return;
    try {
      if (localStorage.getItem(SEEN_KEY) === null) {
        writeSeen(latest);
        setSeen(latest);
      }
    } catch { /* storage blocked — the badge simply stays quiet */ }
  }, [latest, updates.length]);

  const unseen = updates.filter(u => u.number > seen).length;

  const openPanel = () => {
    setOpen(true);
    writeSeen(latest);
    setSeen(latest);   // clear the badge as soon as it has been read
  };

  // Close on Escape, the same affordance the other modals give.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  if (updates.length === 0) return null;

  const dateFmt = (d) => new Date(d + 'T00:00:00')
    .toLocaleDateString(isAr ? 'ar-EG' : 'en-GB',
      { year: 'numeric', month: 'long', day: 'numeric' });

  return (
    <>
      <button
        onClick={openPanel}
        title={isAr ? 'جديد المنصة' : "What's new"}
        aria-label={isAr ? 'جديد المنصة' : "What's new"}
        style={{
          position: 'relative', background: 'rgba(45,58,40,0.08)',
          border: '1.5px solid rgba(45,58,40,0.15)', color: 'var(--sedra-forest)',
          padding: isMobile ? '8px 10px' : '9px 12px', borderRadius: 100,
          cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5,
          fontSize: 13, fontWeight: 700,
        }}>
        <Icon name="Megaphone" size={16} />
        {unseen > 0 && (
          <span style={{
            position: 'absolute', top: -4, insetInlineEnd: -4, minWidth: 18, height: 18,
            padding: '0 5px', borderRadius: 999, background: 'var(--sedra-pistachio-deep)',
            color: 'var(--sedra-cream)', fontSize: 11, fontWeight: 800,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            border: '2px solid var(--sedra-cream)',
          }}>{unseen}</span>
        )}
      </button>

      {/* Portalled to <body> deliberately. This button lives inside the sticky
          header, which sets backdrop-filter: blur(16px) — and a filtered
          ancestor becomes the containing block for position:fixed, so an
          overlay rendered in place would be trapped in the 72px header strip
          instead of covering the viewport. */}
      {open && createPortal((
        <div
          onClick={e => { if (e.target === e.currentTarget) setOpen(false); }}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(30,40,26,0.72)',
            backdropFilter: 'blur(4px)', zIndex: 2000,
            display: 'flex', alignItems: isMobile ? 'flex-end' : 'center',
            justifyContent: 'center', padding: isMobile ? 0 : 20,
          }}>
          <div style={{
            background: '#fff',
            borderRadius: isMobile ? '16px 16px 0 0' : 16,
            padding: isMobile ? '24px 20px' : '32px 34px',
            width: '100%', maxWidth: 560, boxShadow: '0 24px 64px rgba(0,0,0,0.28)',
            fontFamily: isAr ? "'Tajawal', sans-serif" : "'Inter', sans-serif",
            direction: isAr ? 'rtl' : 'ltr',
            maxHeight: isMobile ? '85vh' : '90vh', overflowY: 'auto',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 22 }}>
              <div>
                <div style={{
                  fontSize: 21, fontWeight: 800, color: 'var(--sedra-forest)',
                  marginBottom: 4, display: 'flex', alignItems: 'center', gap: 8,
                  fontFamily: isAr ? "'Almarai', 'Tajawal', sans-serif" : undefined,
                }}>
                  <Icon name="Megaphone" size={19} /> {isAr ? 'جديد المنصة' : "What's new"}
                </div>
                <div style={{ fontSize: 13, color: '#888' }}>
                  {isAr ? 'آخر التحديثات على منصة الجملة' : 'Recent updates to the wholesale platform'}
                </div>
              </div>
              <button onClick={() => setOpen(false)} aria-label={isAr ? 'إغلاق' : 'Close'}
                style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: '#aaa', lineHeight: 1, padding: '0 4px' }}>
                ×
              </button>
            </div>

            {updates.map((u, i) => (
              <div key={u.number} style={{
                paddingBottom: 20, marginBottom: 20,
                borderBottom: i === updates.length - 1 ? 'none' : '1px solid #EFEDE6',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
                  <span style={{
                    background: 'var(--sedra-olive-wash)', color: 'var(--sedra-olive-deep)',
                    borderRadius: 999, padding: '2px 10px', fontSize: 11.5, fontWeight: 800,
                  }}>
                    {isAr ? `تحديث رقم ${u.number}` : `Update #${u.number}`}
                  </span>
                  <span style={{ fontSize: 12, color: '#9A9A8C' }}>{dateFmt(u.date)}</span>
                </div>
                <div style={{
                  fontSize: 16, fontWeight: 800, color: 'var(--sedra-forest)', marginBottom: 8,
                  fontFamily: isAr ? "'Almarai', 'Tajawal', sans-serif" : undefined,
                }}>
                  {u.title[lang] || u.title.en}
                </div>
                <ul style={{ margin: 0, paddingInlineStart: 20, color: '#555', fontSize: 13.5, lineHeight: 1.85 }}>
                  {(u.body[lang] || u.body.en).map((line, k) => <li key={k}>{line}</li>)}
                </ul>
              </div>
            ))}
          </div>
        </div>
      ), document.body)}
    </>
  );
}
